import { createSlice } from '@reduxjs/toolkit';
import { TimeSpan } from '../../@types/dashboard';
import { Campaign, CampaignState, CampaignSummary } from '../../@types/campaign';
import axios from '../../utils/axios';
import { dispatch, store } from '../store';
import { getFromToFilterFromTimespan } from 'components/util/datePicker/FromToDatePicker';
import { GridFilterModel, GridSortModel } from '@mui/x-data-grid';
import {
  combineFilters,
  getFilterQueryParam,
  getOrderbyQueryParam,
  getSkipQueryParam,
  getTopQueryParam
} from 'utils/getQueryParams';
import { translator } from 'translation/useTranslator';
import { getDateFromObject } from '@nps.today/component-library';
import { rejectWithError } from '../dispatchError';

const { from: initialFrom, to: initialTo } = getFromToFilterFromTimespan(TimeSpan.ALL_TIME);
export const initialState: CampaignState = {
  isLoading: false,
  isCampaignSummaryLoading: false,
  isSaveLoading: false,
  isDeleteLoading: false,
  campaigns: { skip: 0, take: 10, total: 0, results: [] },
  campaign: null,
  campaignsSummary: [],
  availableCampaignCategories: {},
  lastUpdatedTimestamp: undefined,
  timespan: TimeSpan.ALL_TIME,
  fromDateFilter: initialFrom,
  toDateFilter: initialTo,
  campaignAnalytics: {
    spread: {
      detractors: 0,
      nps: 0,
      passives: 0,
      promoters: 0,
      total: 0
    },
    respondentCount: 0
  },
  count: 0,
  isRemindersScheduledLoading: {},
  remindersScheduled: {
    StartTime: undefined,
    EndTime: undefined,
    SendTime: Date.now(),
    timespan: TimeSpan.LAST_30_DAYS,
    rescheduled: 0,
    newRemindersToSend: 0
  }
};

export const campaignPersistWhitelist = []; //Removed the following, as the persistent state of the timespan causes confusion 'timespan', 'fromDateFilter', 'toDateFilter'

const slice = createSlice({
  name: 'campaign',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // START SAVE/ADD Campaign/Template
    startSaveCampaign(state) {
      state.isSaveLoading = true;
    },

    stopLoading(state) {
      state.isLoading = false;
    },

    // STOP SAVE/ADD Campaign/Template
    stopSaveCampaign(state) {
      state.isSaveLoading = false;
    },

    // START Campaign Summary LOADING
    startLoadingSummary(state) {
      state.isCampaignSummaryLoading = true;
    },

    // START Campaign Summary LOADING
    stopLoadingSummary(state) {
      state.isCampaignSummaryLoading = false;
    },

    setCampaign(state, action) {
      state.campaign = action.payload;
    },

    // GET CAMPAIGNS
    getCampaignsSuccess(state, action) {
      state.isLoading = false;
      state.campaigns = action.payload;
    },

    // GET CAMPAIGN
    getCampaignSuccess(state, action) {
      state.isLoading = false;
      state.campaign = action.payload;
    },

    postCampaignSuccess(state, action) {
      state.isSaveLoading = false;
    },

    putCampaignSuccess(state, action) {
      state.isSaveLoading = false;
    },

    deleteCampaignStartLoading(state) {
      state.isDeleteLoading = true;
    },

    deleteCampaignStopLoading(state) {
      state.isDeleteLoading = false;
    },

    setCampaignsSummary(state, action) {
      const sortedCampaignsSummary = action.payload as CampaignSummary[];
      state.campaignsSummary =
        sortedCampaignsSummary?.toSorted(function (a, b) {
          if (a?.created < b?.created) {
            return 1;
          }
          if (a?.created > b?.created) {
            return -1;
          }
          return 0;
        }) || [];
    },

    setDateFilter(state, action) {
      state.fromDateFilter = action.payload.from;
      state.toDateFilter = action.payload.to;
      state.timespan = action.payload.timespan;
    },

    setCampaignAnalytics(state, action) {
      state.campaignAnalytics = action.payload;
      state.isLoading = false;
      state.lastUpdatedTimestamp = Date.now();
    },

    setAvailableCampaignCategories(state, action) {
      state.availableCampaignCategories[action.payload.campaignId] = action.payload.categories;
    },

    addAvailableCampaignCategories(state, action) {
      state.availableCampaignCategories[action.payload.campaignId] = [
        ...state.availableCampaignCategories[action.payload.campaignId],
        action.payload.newCategory
      ];
    },

    setRemindersScheduled(state, action) {
      state.remindersScheduled = { ...state.remindersScheduled, ...action.payload };
    },

    setIsPreviewRemindersScheduledLoading(state, action) {
      state.isRemindersScheduledLoading.preview = action.payload;
    },

    setIsSetScheduledRemindersLoading(state, action) {
      state.isRemindersScheduledLoading.setReminders = action.payload;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const { getCampaignSuccess, stopLoading, stopSaveCampaign, setRemindersScheduled } =
  slice.actions;

// ----------------------------------------------------------------------

export function getAvailableCampaignCategory(campaignId: number) {
  return async () => {
    try {
      const response = await axios.get(`/campaigns/${campaignId}/categories`);
      dispatch(
        slice.actions.setAvailableCampaignCategories({ campaignId, categories: response.data })
      );
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage:
          translator?.fetchedAvailableCampaignCategories() ??
          'Fetched available campaign categories'
      });
    } catch (error) {
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotFetchAvailableCampaignCategories() ??
          `Could not fetch available campaign categories`
      });
    }
  };
}

export const updateCampaignAnalytics = (id: string | undefined) => {
  const currentState = store.getState().campaign;
  const start = currentState.fromDateFilter ? new Date(currentState.fromDateFilter) : undefined;
  const end = currentState.toDateFilter ? new Date(currentState.toDateFilter) : undefined;
  const params = {
    start,
    end,
    campaignId: id
  };
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`/analytics/responses`, { params });
      dispatch(slice.actions.setCampaignAnalytics(response.data));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage:
          translator?.fetchedResponseAnalytics() ?? 'Response analytics fetched'
      });
    } catch (error: any) {
      dispatch(slice.actions.stopLoading());
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotFetchResponseAnalytics() ?? `Could not fetch response analytics`
      });
    }
  };
};

export const updateDateFilter = ({
  from,
  to,
  timespan
}: {
  from: any;
  to: any;
  timespan: TimeSpan;
}) => {
  return () => {
    dispatch(
      slice.actions.setDateFilter({
        from: getDateFromObject(from),
        to: getDateFromObject(to),
        timespan: timespan
      })
    );
    return Promise.resolve({
      defaultSuccessMessage:
        translator?.campaignDateFilterUpdated() ?? 'Campaign date filter updated'
    });
  };
};

export function resetCampaign() {
  return async () => {
    dispatch(slice.actions.setCampaign(null));
    return {
      defaultSuccessMessage: translator?.campaignReset() ?? 'Campaign reset'
    };
  };
}

export function getCampaign(id: string | undefined) {
  return async () => {
    if (!id) {
      dispatch(slice.actions.getCampaignSuccess(null));
      return {
        defaultSuccessMessage: translator?.campaignCleared() ?? 'Campaign cleared'
      };
    }
    dispatch(slice.actions.startLoading());

    /**
     * Look for campaign, in already fetched campaigns. This ensures that the campaign data is showed is soon as posible.
     * Still fetch the campaign again, to ensure that the data is up to date.
     */
    const currentState = store.getState().campaign;
    const campaignInStore = currentState.campaigns?.results?.find(
      (campaign) => campaign.id === Number(id)
    );
    if (campaignInStore) {
      dispatch(slice.actions.setCampaign(campaignInStore));
    }

    try {
      const response = await axios.get('/campaigns/' + id);
      dispatch(slice.actions.getCampaignSuccess(response.data));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.campaignFetched() ?? 'Campaign fetched'
      });
    } catch (error: any) {
      dispatch(slice.actions.stopLoading());
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotGetCampaign(id) ?? `Could not get campaign with id ${id}`
      });
    }
  };
}

export function getCampaignsList({
  sortModel,
  filterModel,
  enforcedFilterModel,
  modifyFilterModel = (filterModel) => filterModel,
  top,
  skip
}: {
  sortModel: GridSortModel | undefined;
  filterModel: GridFilterModel | undefined;
  enforcedFilterModel?: GridFilterModel;
  modifyFilterModel?: (newModel?: GridFilterModel) => GridFilterModel | undefined;
  top: number;
  skip: number;
}) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      //Sort model items, with the column name ending on '_analytics', should be removed from the oData query params,
      //and instead be used for the 'analyticsOrderBy' query param
      let campaignSortModel: GridSortModel = [];
      let analyticsSortModel: GridSortModel = [];
      sortModel?.forEach((sortField) => {
        if (sortField.field?.endsWith('_analytics')) {
          analyticsSortModel = [
            ...analyticsSortModel,
            {
              ...sortField,
              field: sortField.field?.replace('_analytics', '')
            }
          ];
        } else {
          campaignSortModel = [...campaignSortModel, sortField];
        }
      });

      const filter = combineFilters({
        filters: [
          getFilterQueryParam(modifyFilterModel(filterModel)),
          getFilterQueryParam(enforcedFilterModel)
        ],
        operator: 'and'
      });

      const response = await axios.get(
        `/campaigns/list?analyticsOrderBy=${getOrderbyQueryParam(
          analyticsSortModel,
          true,
          true
        )}${getOrderbyQueryParam(campaignSortModel)}${filter}${getSkipQueryParam(
          skip
        )}${getTopQueryParam(top)}`
      );
      dispatch(slice.actions.getCampaignsSuccess(response.data));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.campaignsFetched() ?? 'Campaigns fetched'
      });
    } catch (error) {
      dispatch(slice.actions.stopLoading());
      return await rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotFetchCampaigns() ?? `Could not fetch campaigns`
      });
    }
  };
}

export function postCampaign(campaign: Campaign) {
  return async () => {
    dispatch(slice.actions.startSaveCampaign());
    const response = await axios.post('/campaigns', campaign).catch((error: any) => {
      dispatch(slice.actions.stopSaveCampaign());
      return rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotCreateCampaign() ?? `Could not create new campaign`
      });
    });
    dispatch(slice.actions.postCampaignSuccess(response.data));
    return {
      result: response,
      defaultSuccessMessage:
        translator?.campaignCreated(campaign.name) ?? `Campaign created with name ${campaign.name}`
    };
  };
}

export function putCampaign(campaign: Campaign) {
  return async () => {
    dispatch(slice.actions.startSaveCampaign());

    try {
      let response = await axios.put('/v2/campaigns/' + campaign.id, campaign);
      dispatch(slice.actions.putCampaignSuccess(response?.data));
      return {
        result: response,
        defaultSuccessMessage: translator?.campaignSaved() ?? 'Campaign saved'
      };
    } catch (error: any) {
      dispatch(slice.actions.stopSaveCampaign());
      return rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotSaveCampaign() ?? 'Could not save campaign'
      });
    }
  };
}

export function deleteCampaign({ campaign }: { campaign: Campaign }) {
  return async () => {
    dispatch(slice.actions.deleteCampaignStartLoading());
    try {
      await axios.delete(`/campaigns/${campaign.id}`);
      dispatch(slice.actions.deleteCampaignStopLoading());
      return await Promise.resolve({
        defaultSuccessMessage:
          translator?.campaignDeleted(campaign?.name ?? 'Campaign') ??
          `${campaign?.name ?? 'Campaign'} deleted.`
      });
    } catch (error: any) {
      dispatch(slice.actions.deleteCampaignStopLoading());
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotDeleteCampaign(campaign?.name ?? 'Campaign') ??
          `${campaign?.name ?? 'Campaign'} was not deleted.`
      });
    }
  };
}

export function deleteTemplates({ ids }: { ids: number[] }) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      for (let id of ids) {
        await axios.delete('/templates/' + id);
      }
      dispatch(slice.actions.stopLoading());
      return await Promise.resolve({
        defaultSuccessMessage:
          translator?.selectedTemplatesDeleted() ?? 'Selected templates have been deleted'
      });
    } catch (error: any) {
      dispatch(slice.actions.stopLoading());
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotDeleteSelectedTemplates() ?? 'Could not delete selected templates'
      });
    }
  };
}

export function addCategoryToCompaign({
  campaignId,
  templateId,
  newCategory
}: {
  campaignId: number;
  templateId: string | undefined;
  newCategory: string;
}) {
  return async () => {
    if (!templateId) {
      return rejectWithError({
        defaultErrorMessage:
          translator?.couldNotAddNewCategoryWithoutTemplateId() ??
          'Could not add category, as no template id is available'
      });
    }

    const response = await axios
      .post(`/templates/${templateId}/categories/`, {
        name: newCategory
      })
      .catch((error: any) => {
        return rejectWithError({
          error: error,
          defaultErrorMessage:
            translator?.couldNotAddNewCategory() ?? 'New category could not be added'
        });
      });

    dispatch(
      slice.actions.addAvailableCampaignCategories({ campaignId, newCategory: response.data })
    );
    return {
      result: response,
      defaultSuccessMessage: translator?.newCategoryAdded() ?? 'New category added'
    };
  };
}

export function forwardResponse({
  campaignId,
  campaignMemberId,
  emailTo,
  message
}: {
  campaignId: string;
  campaignMemberId: string;
  emailTo: string;
  message: string | undefined;
}) {
  return async () => {
    await axios
      .post(`/campaigns/${campaignId}/responses/${campaignMemberId}/forward`, {
        emailTo,
        message
      })
      .catch((error: any) => {
        return rejectWithError({
          error: error,
          defaultErrorMessage:
            translator?.couldNotForwardResponse() ?? 'Response could not be forwarded'
        });
      });
    return {
      defaultSuccessMessage: 'Response forwarded'
    };
  };
}

export function cloneCampaign({ campaign }: { campaign: Campaign }) {
  return async () => {
    try {
      await axios.post(`/campaigns/${campaign.id}/clone`);
      return await Promise.resolve({
        defaultSuccessMessage:
          translator?.campaignCloned(campaign?.name ?? 'Campaign') ??
          `${campaign?.name ?? 'Campaign'} cloned.`
      });
    } catch (error: any) {
      return rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotCloneCampaign(campaign?.name ?? 'Campaign') ??
          `${campaign?.name ?? 'Campaign'} was not cloned.`
      });
    }
  };
}

export const getCampaignsSummary = () => {
  return async () => {
    dispatch(slice.actions.startLoadingSummary());
    try {
      const response = await axios.get('/campaigns', {
        params: {
          min: true
        }
      });
      dispatch(slice.actions.setCampaignsSummary(response.data));
      dispatch(slice.actions.stopLoadingSummary());

      return await Promise.resolve({
        result: response,
        defaultSuccessMessage:
          translator?.campaignSummariesUpdated() ?? 'Campaign summaries updated'
      });
    } catch (error) {
      dispatch(slice.actions.stopLoadingSummary());

      return rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotGetCampaignSummary() ?? 'Could not get campaign summary'
      });
    }
  };
};

type GetScheduledRemindersPreviewProps = {
  campaignId: number;
  StartTime?: string;
  EndTime?: string;
};
export const getScheduledRemindersPreview = ({
  campaignId,
  StartTime,
  EndTime
}: GetScheduledRemindersPreviewProps) => {
  return async () => {
    dispatch(slice.actions.setIsPreviewRemindersScheduledLoading(true));
    try {
      const response = await axios.post(`campaigns/${campaignId}/remindersPreview`, {
        StartTime: StartTime ? new Date(StartTime).toISOString() : undefined,
        EndTime: EndTime ? new Date(EndTime).toISOString() : undefined
      });
      dispatch(slice.actions.setRemindersScheduled(response.data));
      dispatch(slice.actions.setIsPreviewRemindersScheduledLoading(false));

      return await Promise.resolve({
        result: response,
        defaultSuccessMessage:
          translator?.campaignMembersForSchedulingOfRemindersFetched() ??
          'Campaign members for scheduling of reminders fetched affected'
      });
    } catch (error) {
      dispatch(slice.actions.setIsPreviewRemindersScheduledLoading(false));

      return rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotGetPreviewOfAffectedCampaignMembers() ??
          'Could not get a preview of affected campaign members'
      });
    }
  };
};

type ScheduleRemindersProps = {
  campaignId: number;
  StartTime?: string;
  EndTime?: string;
  SendTime?: string;
};
export const scheduleReminders = ({
  campaignId,
  StartTime,
  EndTime,
  SendTime
}: ScheduleRemindersProps) => {
  return async () => {
    const currentState = store.getState().campaign;
    const totalScheduled =
      currentState.remindersScheduled.newRemindersToSend +
      currentState.remindersScheduled.rescheduled;

    dispatch(slice.actions.setIsSetScheduledRemindersLoading(true));
    try {
      const response = await axios.post(`campaigns/${campaignId}/reminders`, {
        StartTime: StartTime ? new Date(StartTime).toISOString() : undefined,
        EndTime: EndTime ? new Date(EndTime).toISOString() : undefined,
        SendTime: SendTime ? new Date(SendTime).toISOString() : undefined
      });

      dispatch(slice.actions.setRemindersScheduled(response.data));
      dispatch(slice.actions.setIsSetScheduledRemindersLoading(false));

      return await Promise.resolve({
        result: response,
        defaultSuccessMessage:
          translator?.xRemindersScheduled(`${totalScheduled}`) ??
          `${totalScheduled} reminder${totalScheduled > 1 ? 's' : ''} scheduled`
      });
    } catch (error) {
      dispatch(slice.actions.setIsSetScheduledRemindersLoading(false));

      return rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotScheduleReminders(`${totalScheduled}`) ??
          `Could not schedule reminder${totalScheduled > 1 ? 's' : ''}`
      });
    }
  };
};
